import React from 'react';
import { useAdminFeedback } from './hooks/useAdminFeedback';

const STATUS_COLORS = {
  new: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
  reviewed: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/40',
};

const CAT_COLORS = {
  // Legacy categories — kept so historical feedback rows still render.
  bug: 'bg-red-500/20 text-red-400',
  general: 'bg-gray-500/20 text-gray-300',
  // Session 266: brand-safe reframe.
  suggestion: 'bg-purple-500/20 text-purple-400',
  didnt_feel_right: 'bg-amber-500/20 text-amber-400',
  question: 'bg-blue-500/20 text-blue-400',
  thanks: 'bg-emerald-500/20 text-emerald-400',
};

const CAT_LABELS = {
  bug: 'Bug',
  general: 'General',
  suggestion: 'Suggestion',
  didnt_feel_right: "Didn't feel right",
  question: 'Question',
  thanks: 'Thanks',
};

const FeedbackItem = ({ fb, onUpdateStatus }) => (
  <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${CAT_COLORS[fb.category] || CAT_COLORS.general}`}>{CAT_LABELS[fb.category] || fb.category}</span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${STATUS_COLORS[fb.status] || STATUS_COLORS.new}`}>{fb.status}</span>
          <span className="text-gray-500 text-xs">{fb.page}</span>
        </div>
        <p className="text-white text-sm mb-2">{fb.description}</p>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span>{fb.user_name} ({fb.user_email})</span>
          <span>{new Date(fb.created_at).toLocaleString()}</span>
          {fb.reviewed_by && <span>Reviewed by: {fb.reviewed_by}</span>}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        {fb.status !== 'reviewed' && (
          <button onClick={() => onUpdateStatus(fb.feedback_id, 'reviewed')} className="px-2.5 py-1 rounded text-xs bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/40 transition">Mark Reviewed</button>
        )}
        {fb.status !== 'resolved' && (
          <button onClick={() => onUpdateStatus(fb.feedback_id, 'resolved')} className="px-2.5 py-1 rounded text-xs bg-green-600/20 text-green-400 hover:bg-green-600/40 transition">Resolve</button>
        )}
      </div>
    </div>
  </div>
);

const FeedbackContent = ({ feedback, loading, updateStatus }) => {
  if (loading) return <p className="text-gray-400 text-center py-8">Loading feedback...</p>;
  if (feedback.length === 0) return <p className="text-gray-500 text-center py-8">No feedback submissions yet.</p>;
  return (
    <div className="space-y-3">
      {feedback.map(fb => <FeedbackItem key={fb.feedback_id} fb={fb} onUpdateStatus={updateStatus} />)}
    </div>
  );
};

export const AdminFeedbackPanel = () => {
  const { feedback, loading, statusFilter, setStatusFilter, updateStatus } = useAdminFeedback();

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white">User Feedback</h3>
        <div className="flex gap-2">
          {['all', 'new', 'reviewed', 'resolved'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`feedback-filter-${s}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${statusFilter === s ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <FeedbackContent feedback={feedback} loading={loading} updateStatus={updateStatus} />
    </div>
  );
};
